import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { 
  generateRobustDeviceHash, 
  verifyDeviceStability
} from '../utils/robustDeviceFingerprint';

// Funzioni helper per estrarre informazioni dal browser
function getBrowserName(userAgent: string): string {
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari')) return 'Safari';
  if (userAgent.includes('Edge')) return 'Edge';
  return 'Unknown';
}

function getBrowserVersion(userAgent: string): string {
  const match = userAgent.match(/(Chrome|Firefox|Safari|Edge)\/(\d+\.\d+)/);
  return match ? match[2] : 'Unknown';
}

function getEngineName(userAgent: string): string {
  if (userAgent.includes('Chrome')) return 'Blink';
  if (userAgent.includes('Firefox')) return 'Gecko';
  if (userAgent.includes('Safari')) return 'WebKit';
  return 'Unknown';
}

// Funzione per ottenere informazioni di base del dispositivo
async function getBasicDeviceInfo() {
  const nav = navigator;
  const screen = window.screen;
  
  return {
    platform: nav.platform,
    language: nav.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screenWidth: screen.width,
    screenHeight: screen.height,
    hardwareConcurrency: nav.hardwareConcurrency || 0,
    deviceMemory: (nav as any).deviceMemory || 0,
    maxTouchPoints: nav.maxTouchPoints || 0,
    userAgent: nav.userAgent
  };
}

interface DeviceLimitInfo {
  maxDevices: number;
  usedDevices: number;
  remainingDevices: number;
  activationCode: string;
}

const DeviceInfo: React.FC = () => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceLimitInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentDeviceHash, setCurrentDeviceHash] = useState('');
  const [isHardwareExpanded, setIsHardwareExpanded] = useState(false);
  const [deviceStability, setDeviceStability] = useState<boolean | null>(null);
  const [hardwareDetails, setHardwareDetails] = useState<any>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const fetchDeviceInfo = async () => {
      try {
        if (!user) {
          navigate('/login');
          return;
        }

        // Genera l'hash robusto del dispositivo corrente
        const deviceHash = await generateRobustDeviceHash();
        setCurrentDeviceHash(deviceHash);
        
        // Verifica stabilità del fingerprint
        const isStable = await verifyDeviceStability();
        setDeviceStability(isStable);
        
        // Imposta dettagli hardware di base
        const basicInfo = await getBasicDeviceInfo();
        setHardwareDetails({
          deviceHash,
          isStable,
          timestamp: new Date().toISOString(),
          platform: basicInfo.platform,
          language: basicInfo.language,
          timezone: basicInfo.timezone,
          screenWidth: basicInfo.screenWidth,
          screenHeight: basicInfo.screenHeight,
          pixelRatio: window.devicePixelRatio || 1,
          hardwareConcurrency: basicInfo.hardwareConcurrency,
          deviceMemory: basicInfo.deviceMemory,
          maxTouchPoints: basicInfo.maxTouchPoints,
          browserName: getBrowserName(basicInfo.userAgent),
          browserVersion: getBrowserVersion(basicInfo.userAgent),
          engineName: getEngineName(basicInfo.userAgent),
          webglVendor: 'N/A', // Sarà implementato in futuro
          webglRenderer: 'N/A', // Sarà implementato in futuro
          audioFingerprint: 'N/A', // Sarà implementato in futuro
          fontFingerprint: null, // Sarà implementato in futuro
          pluginFingerprint: null // Sarà implementato in futuro
        });

        // Ottieni le informazioni sui dispositivi dal profilo utente
        const profile = await api.getProfile();
        if (profile.activationCode) {
          setDeviceInfo({
            maxDevices: profile.activationCode.maxDevices,
            usedDevices: profile.activationCode.usedDevices,
            remainingDevices: profile.activationCode.maxDevices - profile.activationCode.usedDevices,
            activationCode: profile.activationCode.code,
          });
        }
      } catch (err: any) {
        console.error('Errore nel caricamento delle informazioni sui dispositivi:', err);
        setError('Errore nel caricamento delle informazioni sui dispositivi');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDeviceInfo();
  }, [user, navigate]);

  const handleContinue = () => {
    navigate('/dashboard');
  };

  const toggleHardwareDetails = () => {
    setIsHardwareExpanded(!isHardwareExpanded);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento informazioni...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 btn-primary"
          >
            Continua alla Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-1 flex flex-col justify-center py-8 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          {/* Logo */}
          <div className="flex justify-center">
            <img 
              src="/logo-live-concorsi.png" 
              alt="Live Concorsi" 
              className="h-12 w-auto"
            />
          </div>

          <h2 className="mt-4 text-center text-2xl font-bold text-gray-900">
            Benvenuto in Live Concorsi!
          </h2>
          <p className="mt-1 text-center text-sm text-gray-600">
            La tua registrazione è stata completata con successo
          </p>
        </div>

        <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-6 px-4 shadow sm:rounded-lg sm:px-8">
          
          {/* Sezione informazioni hardware avanzate */}
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg overflow-hidden">
            {/* Header sempre visibile */}
            <div className="p-4 cursor-pointer" onClick={toggleHardwareDetails}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">
                      Hardware Avanzato Rilevato
                    </h3>
                  </div>
                </div>
                <div className="flex items-center">
                  <svg 
                    className={`h-5 w-5 text-green-600 transition-transform duration-200 ${isHardwareExpanded ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
            
            {/* Contenuto espandibile */}
            <div className={`transition-all duration-300 ease-in-out ${isHardwareExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="px-4 pb-4 border-t border-green-200">
                <div className="mt-4 text-sm text-green-700">
                  <p>Il tuo dispositivo è identificato usando caratteristiche hardware uniche:</p>
                  <ul className="mt-2 list-disc list-inside space-y-1">
                    <li>GPU e driver grafici</li>
                    <li>Caratteristiche audio hardware</li>
                    <li>Font e plugin installati</li>
                    <li>Informazioni batteria e connessione</li>
                    <li>Canvas e WebGL fingerprinting</li>
                  </ul>
                  <p className="mt-2 font-mono text-xs bg-green-100 px-2 py-1 rounded">
                    Hardware ID: {currentDeviceHash.substring(0, 20)}...
                  </p>
                  {deviceStability && (
                    <p className="mt-2 text-xs text-green-600 font-semibold">
                      ✓ Fingerprint stabile e affidabile
                    </p>
                  )}
                </div>
                
                {/* Dettagli completi */}
                {hardwareDetails && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-green-800 mb-3">Dettagli Hardware Completi:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                      <div className="space-y-2">
                        <div>
                          <span className="font-semibold text-green-800">Sistema:</span>
                          <p className="text-green-700">{hardwareDetails.platform || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="font-semibold text-green-800">Browser:</span>
                          <p className="text-green-700">{hardwareDetails.browserName || 'N/A'} {hardwareDetails.browserVersion || ''}</p>
                        </div>
                        <div>
                          <span className="font-semibold text-green-800">Engine:</span>
                          <p className="text-green-700">{hardwareDetails.engineName || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="font-semibold text-green-800">Lingua:</span>
                          <p className="text-green-700">{hardwareDetails.language || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="font-semibold text-green-800">Timezone:</span>
                          <p className="text-green-700">{hardwareDetails.timezone || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <span className="font-semibold text-green-800">Schermo:</span>
                          <p className="text-green-700">{hardwareDetails.screenWidth || 'N/A'}x{hardwareDetails.screenHeight || 'N/A'} @{hardwareDetails.pixelRatio || 'N/A'}x</p>
                        </div>
                        <div>
                          <span className="font-semibold text-green-800">CPU Cores:</span>
                          <p className="text-green-700">{hardwareDetails.hardwareConcurrency || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="font-semibold text-green-800">RAM:</span>
                          <p className="text-green-700">{hardwareDetails.deviceMemory ? `${hardwareDetails.deviceMemory}GB` : 'N/A'}</p>
                        </div>
                        <div>
                          <span className="font-semibold text-green-800">Touch Points:</span>
                          <p className="text-green-700">{hardwareDetails.maxTouchPoints || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <h5 className="text-sm font-medium text-green-800 mb-2">GPU:</h5>
                      <div className="text-xs space-y-1">
                        <div>
                          <span className="font-semibold text-green-800">Vendor:</span>
                          <p className="text-green-700 font-mono">{hardwareDetails.webglVendor || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="font-semibold text-green-800">Renderer:</span>
                          <p className="text-green-700 font-mono">{hardwareDetails.webglRenderer || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <h5 className="text-sm font-medium text-green-800 mb-2">Audio:</h5>
                      <p className="text-xs text-green-700 font-mono bg-green-100 px-2 py-1 rounded break-all">
                        {hardwareDetails.audioFingerprint || 'Audio fingerprint non disponibile'}
                      </p>
                    </div>
                    
                    <div className="mt-4">
                      <h5 className="text-sm font-medium text-green-800 mb-2">Font Installati:</h5>
                      <p className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded">
                        {(() => {
                          if (!hardwareDetails.fontFingerprint) {
                            return 'Font fingerprint non disponibile';
                          }
                          const fonts = hardwareDetails.fontFingerprint.split('|');
                          const displayFonts = fonts.slice(0, 10).join(', ');
                          return displayFonts + (fonts.length > 10 ? '...' : '');
                        })()}
                      </p>
                    </div>
                    
                    <div className="mt-4">
                      <h5 className="text-sm font-medium text-green-800 mb-2">Plugin:</h5>
                      <p className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded">
                        {hardwareDetails.pluginFingerprint || 'Nessun plugin rilevato'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          

          {/* Informazioni sui dispositivi */}
          {deviceInfo && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-lg font-medium text-blue-900 mb-2">
                📱 Limite Dispositivi
              </h3>
              <div className="space-y-2 text-sm text-blue-800">
                <p>
                  <strong>Codice di Attivazione:</strong> {deviceInfo.activationCode}
                </p>
                <p>
                  <strong>Dispositivi Massimi:</strong> {deviceInfo.maxDevices}
                </p>
                <p>
                  <strong>Dispositivi Utilizzati:</strong> {deviceInfo.usedDevices}
                </p>
                <p>
                  <strong>Dispositivi Rimanenti:</strong> {deviceInfo.remainingDevices}
                </p>
              </div>
              <div className="mt-3">
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(deviceInfo.usedDevices / deviceInfo.maxDevices) * 100}%` }}
                  ></div>
                </div>
                <p className="text-xs text-blue-700 mt-1">
                  {deviceInfo.usedDevices} di {deviceInfo.maxDevices} dispositivi utilizzati
                </p>
              </div>
            </div>
          )}

          {/* Avviso importante */}
          <div className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Informazione Importante
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Il tuo codice di attivazione può essere utilizzato su un numero limitato di dispositivi. 
                    Se raggiungi il limite, contatta l'amministratore per assistenza.
                  </p>
                </div>
              </div>
            </div>
          </div>

          </div>
        </div>
      </div>
      
      {/* Pulsante continua ancorato al fondo */}
      <div className="bg-white border-t border-gray-200 px-4 py-4 sm:px-6">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <button
            onClick={handleContinue}
            className="w-full btn-primary"
          >
            Continua alla Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeviceInfo;
